import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, CheckCircle2, AlertCircle } from "lucide-react";

// Fallback if the configured limit hasn't loaded yet.
const DEFAULT_SELLER_CONTRIBUTION = 25;

const fmtMoney = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// Phase 1 — Seller Business Contribution Monitor (calculation + visibility only).
// Fetches monthly UPLOADED-invoice revenue for the selected seller and all sellers, then projects
// the seller's contribution % including the live current-quotation value. No warnings/colors/gating.
export function SellerContributionCard({ sellerId, month, currentQuotationValue, onValidationChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sellerId || !month) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/analytics/seller-contribution`, {
        params: { month, seller_id: sellerId },
        withCredentials: true,
      })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sellerId, month]);

  const sellerRev = Number(data?.seller_revenue || 0);
  const monthlyTarget = data?.monthly_business_target != null ? Number(data.monthly_business_target) : null;
  const limit = data?.seller_contribution_limit != null ? Number(data.seller_contribution_limit) : DEFAULT_SELLER_CONTRIBUTION;
  const qval = Number(currentQuotationValue || 0);
  const projected = monthlyTarget && monthlyTarget > 0 ? ((sellerRev + qval) / monthlyTarget) * 100 : null;
  const exceedsLimit = projected != null && projected > limit;
  const showValidation = Boolean(sellerId) && !loading && projected != null;

  // Notify parent (Create/Edit) so it can gate the Create button / show the approval banner.
  useEffect(() => {
    if (typeof onValidationChange === "function") {
      onValidationChange({ exceeds: exceedsLimit, hasTarget: monthlyTarget != null, projected, limit });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exceedsLimit, monthlyTarget, projected, limit]);

  return (
    <>
    <Card data-testid="seller-contribution-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <PieChart className="h-4 w-4 text-accent" />
          Seller Business Contribution
          {month && <span className="text-xs font-normal text-muted-foreground">({month})</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!sellerId ? (
          <p className="text-sm text-muted-foreground" data-testid="seller-contribution-empty">
            Select a seller to see their projected business contribution.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Monthly Business Target" value={monthlyTarget != null ? fmtMoney(monthlyTarget) : "—"} loading={loading} testId="contribution-monthly-target" />
              <Stat label="Current Seller Revenue" value={fmtMoney(sellerRev)} loading={loading} testId="contribution-seller-revenue" />
              <Stat label="Current Quotation Value" value={fmtMoney(qval)} testId="contribution-quotation-value" />
              <Stat label="Projected Seller Contribution" value={projected != null ? `${projected.toFixed(2)}%` : "—"} highlight testId="contribution-projected-pct" />
            </div>
            {!loading && monthlyTarget == null && (
              <p className="text-[11px] text-amber-600 mt-2" data-testid="contribution-no-target">
                No Monthly Business Target configured for {month}. Set it in Control Panel → Monthly Business Targets.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>

    {/* Phase 2 — live validation indicator (visual only, no gating). */}
    {showValidation && (
      <div
        className={`p-4 rounded-lg border-2 ${exceedsLimit ? "bg-red-50 border-red-300" : "bg-green-50 border-green-300"}`}
        data-testid="seller-contribution-validation-card"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            {exceedsLimit
              ? <AlertCircle className="h-5 w-5 text-red-600" />
              : <CheckCircle2 className="h-5 w-5 text-green-600" />}
            <div>
              <p className="text-sm">
                Monthly Business Target: <span className="font-mono font-medium">{monthlyTarget != null ? fmtMoney(monthlyTarget) : "—"}</span>
              </p>
              <p className="text-sm">
                Allowed Seller Contribution: <span className="font-mono font-medium">{limit.toFixed(2)}%</span>
              </p>
              <p className="text-sm">
                Projected Seller Contribution: <span className="font-mono font-semibold" data-testid="contribution-validation-projected">{projected.toFixed(2)}%</span>
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-lg font-bold ${exceedsLimit ? "text-red-600" : "text-green-700"}`} data-testid="contribution-validation-status">
              {exceedsLimit ? "🔴 Approval Required" : "🟢 Within Limit"}
            </p>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function Stat({ label, value, loading, highlight, testId }) {
  return (
    <div className={`p-3 rounded-lg border ${highlight ? "bg-accent/5 border-accent/30" : "bg-slate-50 border-slate-200"}`}>
      <p className="text-[11px] text-muted-foreground leading-tight">{label}</p>
      <p className={`font-mono font-semibold mt-1 ${highlight ? "text-accent text-lg" : "text-[#0F0F0F]"}`} data-testid={testId}>
        {loading ? "…" : value}
      </p>
    </div>
  );
}
